import { useCallback, useEffect } from 'react';
import { useMarketStore, useAuthStore } from '../store';
import { getUserWatchlists, updateWatchlist, createWatchlist } from '../utils/api';

// Tier level mapping
const tierLevel: Record<string, number> = {
  'free': 0,
  'starter': 1,
  'early_supporter': 1,
  'pro': 2,
  'enterprise': 3
};

// Tier limits
const TIER_LIMITS = {
  free: { watchlist_enabled: false, max_tickers: 0 },
  starter: { watchlist_enabled: true, max_tickers: 10 },
  early_supporter: { watchlist_enabled: true, max_tickers: 10 },
  pro: { watchlist_enabled: true, max_tickers: null }, // unlimited
  enterprise: { watchlist_enabled: true, max_tickers: null },
};

export function useWatchlist() {
  const { user } = useAuthStore();
  const {
    watchlist,
    userWatchlists,
    activeWatchlistId,
    watchlistLoading,
    setWatchlist,
    setUserWatchlists,
    setActiveWatchlist,
    setWatchlistLoading,
    addToWatchlist: addToLocalWatchlist,
    removeFromWatchlist: removeFromLocalWatchlist,
  } = useMarketStore();

  const userTier = user?.subscription_tier || 'free';
  const userLevel = tierLevel[userTier] || 0;
  const hasWatchlistAccess = userLevel >= 1;
  const limits = TIER_LIMITS[userTier as keyof typeof TIER_LIMITS] || TIER_LIMITS.free;
  const maxTickers = limits.max_tickers;

  // Check if at or near limit
  const atLimit = maxTickers !== null && watchlist.length >= maxTickers;
  const nearLimit = maxTickers !== null && watchlist.length >= maxTickers - 2;

  // Check if a ticker is in the watchlist
  const isInWatchlist = useCallback((ticker: string) => {
    return watchlist.includes(ticker.toUpperCase());
  }, [watchlist]);

  // Fetch watchlists from backend and sync local state
  const fetchWatchlists = useCallback(async () => {
    if (!user || !hasWatchlistAccess) {
      // Clear watchlist for free users or logged out users
      setWatchlist([]);
      setUserWatchlists([]);
      return;
    }

    try {
      setWatchlistLoading(true);
      const response = await getUserWatchlists();
      setUserWatchlists(response.watchlists);

      // Sync local watchlist from backend
      const defaultWatchlist = response.watchlists.find(w => w.is_default);
      if (defaultWatchlist) {
        setWatchlist(defaultWatchlist.tickers);
        setActiveWatchlist(defaultWatchlist.id);
      } else if (response.watchlists.length > 0) {
        setWatchlist(response.watchlists[0].tickers);
        setActiveWatchlist(response.watchlists[0].id);
      } else {
        // No watchlists exist, create a default one
        try {
          const newWatchlist = await createWatchlist('Default', []);
          setUserWatchlists([newWatchlist]);
          setWatchlist(newWatchlist.tickers);
          setActiveWatchlist(newWatchlist.id);
        } catch {
          // Ignore error if we can't create a watchlist
        }
      }
    } catch (error) {
      console.error('Failed to fetch watchlists:', error);
    } finally {
      setWatchlistLoading(false);
    }
  }, [user, hasWatchlistAccess, setWatchlist, setUserWatchlists, setActiveWatchlist, setWatchlistLoading]);

  // Add ticker to watchlist (syncs with backend)
  const addTicker = useCallback(async (ticker: string): Promise<boolean> => {
    if (!user || !hasWatchlistAccess) {
      return false;
    }

    const upperTicker = ticker.toUpperCase();

    // Check if already in watchlist
    if (watchlist.includes(upperTicker)) {
      return true;
    }

    // Check limit
    if (maxTickers !== null && watchlist.length >= maxTickers) {
      return false;
    }

    // Optimistically update local state
    addToLocalWatchlist(upperTicker);

    // Update backend
    if (activeWatchlistId) {
      try {
        const newTickers = [...watchlist, upperTicker];
        const updated = await updateWatchlist(activeWatchlistId, { tickers: newTickers });

        // Update userWatchlists with the new data
        setUserWatchlists(
          userWatchlists.map(w => w.id === activeWatchlistId ? updated : w)
        );
        return true;
      } catch (error) {
        console.error('Failed to add ticker to watchlist:', error);
        // Rollback on error
        removeFromLocalWatchlist(upperTicker);
        return false;
      }
    }

    return true;
  }, [user, hasWatchlistAccess, watchlist, maxTickers, activeWatchlistId, userWatchlists, addToLocalWatchlist, removeFromLocalWatchlist, setUserWatchlists]);

  // Remove ticker from watchlist (syncs with backend)
  const removeTicker = useCallback(async (ticker: string): Promise<boolean> => {
    if (!user || !hasWatchlistAccess) {
      return false;
    }

    const upperTicker = ticker.toUpperCase();

    // Check if in watchlist
    if (!watchlist.includes(upperTicker)) {
      return true;
    }

    // Optimistically update local state
    removeFromLocalWatchlist(upperTicker);

    // Update backend
    if (activeWatchlistId) {
      try {
        const newTickers = watchlist.filter(t => t !== upperTicker);
        const updated = await updateWatchlist(activeWatchlistId, { tickers: newTickers });

        // Update userWatchlists with the new data
        setUserWatchlists(
          userWatchlists.map(w => w.id === activeWatchlistId ? updated : w)
        );
        return true;
      } catch (error) {
        console.error('Failed to remove ticker from watchlist:', error);
        // Rollback on error
        addToLocalWatchlist(upperTicker);
        return false;
      }
    }

    return true;
  }, [user, hasWatchlistAccess, watchlist, activeWatchlistId, userWatchlists, addToLocalWatchlist, removeFromLocalWatchlist, setUserWatchlists]);

  // Toggle ticker in watchlist
  const toggleTicker = useCallback(async (ticker: string): Promise<boolean> => {
    if (isInWatchlist(ticker)) {
      return removeTicker(ticker);
    } else {
      return addTicker(ticker);
    }
  }, [isInWatchlist, addTicker, removeTicker]);

  // Fetch watchlists when user changes
  useEffect(() => {
    if (user && hasWatchlistAccess) {
      fetchWatchlists();
    } else if (!user) {
      // Clear watchlist when logged out
      setWatchlist([]);
      setUserWatchlists([]);
      setActiveWatchlist(null);
    }
  }, [user?.id, hasWatchlistAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // State
    watchlist,
    userWatchlists,
    activeWatchlistId,
    loading: watchlistLoading,

    // Access control
    hasWatchlistAccess,
    maxTickers,
    atLimit,
    nearLimit,

    // Actions
    isInWatchlist,
    addTicker,
    removeTicker,
    toggleTicker,
    fetchWatchlists,

    // User tier info
    userTier,
    userLevel,
  };
}
